import React, { useRef } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Heading,
  IconButton,
  Paragraph,
  Popover,
  Table,
  Text,
  Tooltip,
} from '@zillow/constellation-10';
import { IconInfoFilled } from '@zillow/constellation-icons';

import { LoadingParagraph } from '@zg-rentals/loading-components';
import { AccountViewListingStatus, ListingType, PropertyType } from '@zg-rentals/ts-rental-manager-api-client';

import { useTableObserver } from '../../../hooks/queries/properties/useTableObserver';
import {
  useGetPropertiesData,
  useIsSingleFamilyEnterpriseExperience,
  useZramAdvertisingPages,
} from '../../../hooks/queries/properties/zram';
import { useScreenSize } from '../../../hooks/useScreenSize';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import { useAuth } from '../../../components/AuthProvider';
import ClaimYourListingDetails from '../../../components/ClaimYourListingDetails';
import { FixedScrollbar } from '../../../components/properties/FixedScrollbar';
import { NoSearchResults } from '../../../components/properties/NoSearchResults';
import { stickyLeftCellRecipe } from '../../../components/properties/styles';
import { ZRMOverview } from '../../../components/properties/ZRMOverview';

import { errorContainerStyles, styledHeaderCellStyles, tableWrapperStyles } from '../styles';

import { useAutoSpotlight } from '../AutoSpotlightPanel/AutoSpotlightContext';
import { PropertyRow } from '../PropertyRow';

import type { AccountViewListingDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FilterState } from '../../../utils/propertiesPageUtils';

const SKELETON_LISTING: AccountViewListingDto = {
  listingAlias: '',
  name: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    unit: '',
  },
  photoKey: '',
  propertyType: PropertyType.UNKNOWN,
  listingType: ListingType.RENTAL,
  isMultifamily: false,
  status: {
    status: AccountViewListingStatus.PUBLISHED,
    text: 'Published',
    tagAppearance: 'success',
  },
  updatedMs: 0,
};

export const PropertyTable = ({
  filters,
  setFilters,
  isSkeletonView,
  showAutoSpotlightControl = false,
  showSmartAssignControl = false,
}: {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  isSkeletonView: boolean;
  showAutoSpotlightControl?: boolean;
  showSmartAssignControl?: boolean;
}) => {
  const { user } = useAuth();
  const isSingleFamilyEnterprise = useIsSingleFamilyEnterpriseExperience();
  const { isScreenMobile } = useScreenSize();
  const spotlightCtx = useAutoSpotlight();
  const showSpotlightControls = showAutoSpotlightControl || showSmartAssignControl;
  const isSelectionMode = showSpotlightControls && Boolean(spotlightCtx?.isSelectionMode);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { isScrolledToLeft, showFixedScrollbar, isTableBottomVisible } = useTableObserver({
    scrollContainerRef,
    isScreenMobile,
  });

  const {
    data: { listings = [], listingAliasPages = [], listingCount } = {},
    isError,
    isPending: isPropertiesPending,
    isPlaceholderData: isShowingPreviousFilterData,
  } = useGetPropertiesData(filters);

  const { archived: numArchivedListings = 0 } = listingCount || {};

  const isArchivedView = filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED;

  // Skeleton only before the list first loads (or on the explicit opt-ins). A background re-read — a spotlight
  // change re-fetching the list, loading the next page — leaves the rows on screen and updates them in place, so
  // the table never flashes to skeleton mid-interaction. isSkeletonView (filter change) and the RECE-2031 Pinned ->
  // Smart Assign revert still force the loading view on purpose.
  // Changing the search text or the tab swaps in a placeholder that carries counts but no listings, so it has to read
  // as loading: rendering it would otherwise show the empty state over a list that is still on its way.
  const isPageLoading =
    isPropertiesPending ||
    isShowingPreviousFilterData ||
    isSkeletonView ||
    Boolean(spotlightCtx?.isRevertingPinPending);
  const listingsToRender = isPageLoading
    ? Array(PropertiesConstants.PROPERTIES_LIMIT_DEFAULT).fill(SKELETON_LISTING)
    : listings;

  // Keyed off the loaded list, not the rows on screen, so a skeleton pass keeps the page read alive for the refresh
  // it is waiting on.
  const isAdvertisingReadByPage =
    Boolean(isSingleFamilyEnterprise) && !isArchivedView && !isPropertiesPending && !isShowingPreviousFilterData;
  const advertisingPageStateByListingAlias = useZramAdvertisingPages(isAdvertisingReadByPage ? listingAliasPages : [], {
    enabled: isAdvertisingReadByPage,
  });

  const renderError = () => (
    <Box css={errorContainerStyles}>
      We are having trouble retrieving your properties at this time. Please refresh the page or check back soon.
    </Box>
  );

  const renderInfoPopover = (header: string, body: string) => (
    <Popover
      body={<Paragraph>{body}</Paragraph>}
      trigger={
        <IconButton
          title={header}
          size="sm"
          emphasis="bare"
          shape="circle"
          css={{
            color: 'icon.muted',
            width: 'icon.sm',
            height: 'icon.sm',
            verticalAlign: 'text-top',
            marginLeft: '100',
          }}
          icon={<IconInfoFilled />}
          onClick={(e) => e.stopPropagation()}
        />
      }
      header={<Heading level={4}>{header}</Heading>}
    />
  );

  const renderNoListings = () => {
    if (filters.searchText) {
      return (
        <NoSearchResults
          showArchived={filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED}
          onRemoveSearchTextClick={() => {
            setFilters({ ...filters, searchText: '' });
          }}
        />
      );
    } else if (filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED) {
      return <Text>You don&apos;t have any archived properties.</Text>;
    } else if (filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.SPOTLIGHT) {
      return (
        <Box css={{ textAlign: 'center' }}>
          <Heading level={2} textStyle="heading-md" fontFamily="heading-alt">
            No spotlighted properties found
          </Heading>
          <Paragraph css={{ marginBlock: '600' }}>
            Once listings are selected for Listing Spotlight, they&apos;ll appear here.
          </Paragraph>
        </Box>
      );
    } else if (filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED) {
      return (
        <Box css={{ textAlign: 'center' }}>
          <Heading level={2} textStyle="heading-md" fontFamily="heading-alt">
            Claim and list your properties for free
          </Heading>
          <Paragraph css={{ marginBlock: '600' }}>
            Help renters discover your unadvertised properties in just a few steps.
          </Paragraph>
          <Button tone="brand" emphasis="filled" asChild={true} css={{ marginBottom: '1200' }}>
            {/* oxlint-disable-next-line next/no-html-link-for-pages -- cross-app navigation to ad-management-web, not this app's own route */}
            <a href="/rental-manager/ad-management/how-claiming-works">Start claiming</a>
          </Button>
          <ClaimYourListingDetails />
          <Paragraph css={{ marginTop: '1200' }}>
            Unclaimed properties with under 25 units may already be listed on Zillow, Trulia and HotPads.
          </Paragraph>
          <Paragraph>Claim yours to manage your portfolio in one place and move faster to fill vacancies.</Paragraph>
        </Box>
      );
    } else if (user?.features?.canAddListing) {
      return (
        <ZRMOverview
          hasAnyArchivedListings={numArchivedListings > 0}
          handleShowArchivedClick={() =>
            setFilters({ ...filters, subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED })
          }
        />
      );
    }
  };

  const renderTable = () => (
    <>
      {isArchivedView && (
        <LoadingParagraph css={{ marginTop: '600' }} isLoading={isPageLoading} loadingWidth="10rem">
          {numArchivedListings} {numArchivedListings > 1 ? 'properties' : 'property'}
        </LoadingParagraph>
      )}
      <Box css={tableWrapperStyles.raw({ shouldShowBorder: isScreenMobile && isScrolledToLeft })}>
        <Box
          ref={scrollContainerRef}
          css={{
            overflowX: { base: 'hidden', lg: 'auto' },
            maxWidth: '100%',
          }}
        >
          <Table.Root
            aria-label="Properties list"
            css={{
              minWidth: isArchivedView || isSingleFamilyEnterprise ? '100%' : { lg: '1550px' },
            }}
            appearance="horizontal"
          >
            <Table.Header css={{ display: { base: 'none', lg: 'table-header-group' } }}>
              <Table.Row>
                {isSelectionMode && (
                  // oxlint-disable-next-line constellation/no-raw-spacing-values
                  <Table.HeaderCell css={{ ...styledHeaderCellStyles, width: '40px', padding: '0 0 0 200' }}>
                    <Tooltip
                      content="Selects only the listings on this page"
                      trigger={
                        <Checkbox
                          checked={spotlightCtx?.isAllOnPageSelected ?? false}
                          onChange={() => spotlightCtx?.toggleSelectAllOnPage()}
                          aria-label="Select all listings on this page"
                        />
                      }
                    />
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell
                  css={{ ...styledHeaderCellStyles, ...stickyLeftCellRecipe.raw({ scrolled: isScrolledToLeft }) }}
                >
                  Property address
                </Table.HeaderCell>
                <Table.HeaderCell css={styledHeaderCellStyles}>Status</Table.HeaderCell>
                {!isArchivedView && (
                  <>
                    {!isSingleFamilyEnterprise && (
                      <>
                        <Table.HeaderCell css={styledHeaderCellStyles}>
                          <Paragraph textStyle="body-sm-bold" css={{ minWidth: 'max-content' }}>
                            Listing quality score
                            {renderInfoPopover(
                              'Listing quality score',
                              'Score reflects unit-level availability and completeness of key property details, weighted using Zillow internal data',
                            )}
                          </Paragraph>
                        </Table.HeaderCell>
                        <Table.HeaderCell css={styledHeaderCellStyles}>
                          <Paragraph textStyle="body-sm-bold" css={{ minWidth: 'max-content' }}>
                            Missing details
                            {renderInfoPopover(
                              'Missing details',
                              'Top recommended details to add, based on Zillow internal data. These details are what renters most care about.',
                            )}
                          </Paragraph>
                        </Table.HeaderCell>
                      </>
                    )}
                    <Table.HeaderCell css={styledHeaderCellStyles}>
                      <Paragraph textStyle="body-sm-bold" css={{ minWidth: 'max-content' }}>
                        Prospects
                        {renderInfoPopover(
                          'Total prospects',
                          'Includes phone leads, email leads, and website referrals',
                        )}
                      </Paragraph>
                    </Table.HeaderCell>
                    <Table.HeaderCell css={styledHeaderCellStyles}>Advertising</Table.HeaderCell>
                    <Table.HeaderCell css={styledHeaderCellStyles}>
                      {showSpotlightControls ? 'Action' : 'Actions'}
                    </Table.HeaderCell>
                  </>
                )}
                <Table.HeaderCell css={styledHeaderCellStyles}>Source</Table.HeaderCell>
                <Table.HeaderCell css={styledHeaderCellStyles}> </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {listingsToRender.map((listing, index) => (
                <PropertyRow
                  key={isPageLoading ? `skeleton-${index}` : listing?.listingAlias}
                  listing={listing}
                  isLoading={isPageLoading}
                  isScrolledToLeft={isScrolledToLeft}
                  isArchivedView={isArchivedView}
                  advertisingPageState={advertisingPageStateByListingAlias.get(listing?.listingAlias)}
                  showAutoSpotlightControl={showAutoSpotlightControl}
                  showSmartAssignControl={showSmartAssignControl}
                />
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>
      <FixedScrollbar
        scrollContainerRef={scrollContainerRef}
        isVisible={showFixedScrollbar}
        isTableBottomVisible={isTableBottomVisible}
      />
    </>
  );

  return (
    <>
      {isError && renderError()}
      {!isError && !isPageLoading && listingsToRender.length === 0 && (
        <Box css={errorContainerStyles}>{renderNoListings()}</Box>
      )}
      {!isError && listingsToRender.length > 0 && renderTable()}
    </>
  );
};
